import React, { useState, useEffect } from 'react';
import { FiUser, FiMapPin, FiCalendar, FiMail, FiHome, FiLock } from 'react-icons/fi';
import { GoHash } from 'react-icons/go';
import { FaGraduationCap, FaCertificate, FaMoneyBillWave, FaRegBuilding } from 'react-icons/fa';
import { MdWork, MdSupervisorAccount } from 'react-icons/md';
import InputField from '../components/ui/InputField';
import ComponentWrapper from '../components/utils/ComponentWrapper';
import ImageUploader from '../components/ui/ImageUploader';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { createEmployee, fetchEmployeesWithManagementPermission, uploadEmployeePhoto } from '../redux/actions/employeeActions';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { fetchUsers } from '../redux/actions/userActions';
import { fetchDesignations } from '../redux/actions/designationActions';
import { forceRefreshDesignations } from '../redux/actions/forceRefreshDesignations';
import { fetchPermissionById } from '../redux/actions/permissionActions';
import axiosInstance from '../axios';

// Define interfaces for form validation
interface FormErrors {
  emailId?: string;
  name?: string;
  password?: string;
  employeeStatus?: string;
  designation?: string;
  address?: string;
  city?: string;
  state?: string;
  pincode?: string;
  photo?: string;
  dateOfBirth?: string;
  joiningDate?: string;
  salary?: string;
  managerId?: string;
}

const AddEmployee = () => {
  // Add Redux hooks
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, error } = useAppSelector(state => state.employees);
  const { user } = useAppSelector(state => state.auth);

  // Permission check - redirect if user doesn't have employeeWrite permission
  useEffect(() => {
    // Function to check if user has employeeWrite permission
    const checkEmployeeWritePermission = () => {
      // If user is superAdmin, they have all permissions
      if (user?.designation === 'superAdmin') {
        return true;
      }
      
      // If no user or no permissions, deny access
      if (!user || !user.permissions) {
        return false;
      }
      
      let hasEmployeeWritePermission = false;
      
      // Check if permissions is an object with numeric keys (like {0: {...}})
      if (typeof user.permissions === 'object' && '0' in user.permissions) {
        //@ts-ignore
        hasEmployeeWritePermission = Boolean(user.permissions[0].employeeWrite);
      }
      // Check if permissions is an array
      else if (Array.isArray(user.permissions) && user.permissions.length > 0) {
        hasEmployeeWritePermission = Boolean(user.permissions[0].employeeWrite);
      }
      // Direct object access if neither of the above
      else if (typeof user.permissions === 'object') {
        hasEmployeeWritePermission = Boolean(user.permissions.employeeWrite);
      }
      
      return hasEmployeeWritePermission;
    };
    
    // If user doesn't have permission, redirect and show toast
    if (!checkEmployeeWritePermission()) {
      toast.error("You don't have permission to create employees");
      navigate('/employees');
    }
  }, [user, navigate]);

  // State for form fields
  const [employeeId, setEmployeeId] = useState('');
  const [emailId, setEmailId] = useState('');
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('India');
  const [state, setState] = useState('');
  const [pincode, setPincode] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [password, setPassword] = useState('');
  const [managerId, setManagerId] = useState('');

  // Job related states
  const [joiningDate, setJoiningDate] = useState('');
  const [designation, setDesignation] = useState('');
  const [salary, setSalary] = useState('');
  const [education, setEducation] = useState('');
  const [certification, setCertification] = useState('');

  // Employee status
  const [employeeStatus, setEmployeeStatus] = useState('');
  const [jobLocation, setJobLocation] = useState('');

  // Photo upload states
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);

  // Form validation states
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Designation permission states
  const [designationPermissions, setDesignationPermissions] = useState<any>(null);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [hasApplicationAccess, setHasApplicationAccess] = useState(false);

  // Default profile picture URL that can be used if no photo is uploaded
  const defaultProfilePicture = 'https://i.pravatar.cc/300';

  // Get designations from Redux
  const { designations, loading: designationsLoading, hasFetched, loading: usersLoading } = useAppSelector(state => state.users);
  const { currentPermission, loading: permissionLoading } = useAppSelector(state => state.permissions);

  // Get managers from Redux
  const { potentialManagers, loadingPotentialManagers } = useAppSelector(state => state.employees);

  // Fetch fresh designations data when component mounts
  useEffect(() => {
    console.log('AddEmployee: Force refreshing designations data on mount');
    dispatch(forceRefreshDesignations());
  }, [dispatch]);

  // Fetch potential managers when component mounts
  useEffect(() => {
    dispatch(fetchEmployeesWithManagementPermission());
  }, [dispatch]);

  // Function to fetch designation permissions
  const fetchDesignationPermissions = async (designationId: string) => {
    if (!designationId) return;
    
    setLoadingPermissions(true);
    try {
      console.log('Fetching permissions for designation ID:', designationId);
      const response = await dispatch(fetchPermissionById(designationId)).unwrap();
      console.log('Designation permissions response:', response);
      
      if (response && response.data) {
        console.log('Designation data:', response.data);
        
        // Handle different data structures that might come from the API
        let permissionsData;
        
        // Check if permissions is an array (like in some API responses)
        if (response.data.permissions && Array.isArray(response.data.permissions) && response.data.permissions.length > 0) {
          console.log('Permissions is an array, using first item:', response.data.permissions[0]);
          permissionsData = response.data.permissions[0];
        } 
        // Check if permissions is a direct object
        else if (response.data.permissions && typeof response.data.permissions === 'object') {
          console.log('Permissions is an object:', response.data.permissions);
          permissionsData = response.data.permissions;
        }
        // Check if the permissions are directly on the data object
        else {
          console.log('Looking for permissions directly on data object');
          permissionsData = response.data;
        }
        
        setDesignationPermissions(permissionsData);
        
        // Check for applicationAccess in various possible locations
        const hasAccess = 
          Boolean(permissionsData?.applicationAccess) || 
          Boolean(response.data?.applicationAccess);
        
        console.log('Application access value determined to be:', hasAccess);
        setHasApplicationAccess(hasAccess);
      } else {
        console.log('No permissions data found in response');
        setDesignationPermissions(null);
        setHasApplicationAccess(false);
      }
    } catch (error) {
      console.error('Error fetching designation permissions:', error);
      toast.error('Failed to fetch designation permissions');
      setDesignationPermissions(null);
      setHasApplicationAccess(false);
    } finally {
      setLoadingPermissions(false);
    }
  };

  // Validate form fields
  const validateField = (name: string, value: string): string | undefined => {
    switch (name) {
      case 'emailId':
        if (!value) return 'Email is required';
        if (!/\S+@\S+\.\S+/.test(value)) return 'Email is invalid';
        return undefined;

      case 'name':
        if (!value) return 'Name is required';
        if (value.length < 3) return 'Name must be at least 3 characters';
        return undefined;

      case 'password':
        if (hasApplicationAccess) {
          if (!value) return 'Password is required';
          if (value.length < 8) return 'Password must be at least 8 characters';
        }
        return undefined;

      case 'employeeStatus':
        if (!value) return 'Status is required';
        // Check if the value is one of the valid statuses
        const validStatuses = ['ACTIVE', 'INACTIVE', 'PROBATION', 'TERMINATED', 'RESIGNED', 'RETIRED', 'ON_LEAVE'];
        if (!validStatuses.includes(value)) return 'Invalid employee status';
        return undefined;

      case 'designation':
        if (!value) return 'Designation is required';
        // Check if the designation exists and has a valid ID
        const designationId = getDesignationId(value);
        if (!designationId) return 'Please select a valid designation';
        return undefined;

      case 'address':
        if (!value) return 'Address is required';
        return undefined;

      case 'city':
        if (!value) return 'City is required';
        return undefined;

      case 'state':
        if (!value) return 'State is required';
        return undefined;

      case 'pincode':
        if (!value) return 'Pin code is required';
        if (!/^\d+$/.test(value)) return 'Pin code should contain only numbers';
        if (value.length !== 6) return 'Pin code must be 6 digits';
        return undefined;

      case 'salary':
        if (value && !/^\d*$/.test(value)) return 'Salary must be a number';
        return undefined;

      case 'dateOfBirth':
        if (!value) return 'Date of birth is required';
        // Validate date format (YYYY-MM-DD)
        if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return 'Invalid date format';
        // Validate year range (1900-2023)
        const dobYear = parseInt(value.split('-')[0], 10);
        if (dobYear < 1900 || dobYear > 2023) return 'Year must be between 1900 and 2023';
        return undefined;
        
      case 'joiningDate':
        if (!value) return 'Date of joining is required';
        // Validate date format (YYYY-MM-DD)
        if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return 'Invalid date format';
        // Validate year range (1900-2100)
        const joinYear = parseInt(value.split('-')[0], 10);
        if (joinYear < 1900 || joinYear > 2100) return 'Year must be between 1900 and 2100';
        return undefined;

      case 'managerId':
        // Manager is optional
        return undefined;

      default:
        return undefined;
    }
  };

  // Handle field blur for validation
  const handleBlur = (field: string, value: string) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    const error = validateField(field, value);
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  // Handle form input change with validation
  const handleInputChange = (field: string, value: string) => {
    // Special handling for pincode to only allow numbers
    if (field === 'pincode') {
      // Only allow numeric characters
      if (!/^\d*$/.test(value)) {
        return;
      }
      // Limit to 6 digits
      if (value.length > 6) {
        return;
      }
    }

    // Special handling for salary to only allow numbers
    if (field === 'salary' && !/^\d*$/.test(value)) {
      return;
    }

    // Special handling for date fields to prevent invalid dates with 6-digit years
    if (field === 'dateOfBirth' || field === 'joiningDate') {
      // If the value doesn't match YYYY-MM-DD format or has more than 4 digits for year
      if (value && !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        // If user is trying to input a year with more than 4 digits, reject it
        if (value.split('-')[0]?.length > 4) {
          return;
        }
      }
    }

    // Update the field value
    switch (field) {
      case 'emailId':
        setEmailId(value);
        break;
      case 'name':
        setName(value);
        break;
      case 'address':
        setAddress(value);
        break;
      case 'city':
        setCity(value);
        break;
      case 'state':
        setState(value);
        break;
      case 'pincode':
        setPincode(value);
        break;
      case 'dateOfBirth':
        setDateOfBirth(value);
        break;
      case 'password':
        setPassword(value);
        break;
      case 'joiningDate':
        setJoiningDate(value);
        break;
      case 'salary':
        setSalary(value);
        break;
      case 'education':
        setEducation(value);
        break;
      case 'certification':
        setCertification(value);
        break;
      case 'employeeStatus':
        setEmployeeStatus(value);
        break;
      case 'jobLocation':
        setJobLocation(value);
        break;
      case 'managerId':
        setManagerId(value);
        break;
    }

    // Update errors if the field has been touched
    if (touched[field]) {
      const error = validateField(field, value);
      setErrors(prev => ({ ...prev, [field]: error }));
    }
  };

  // Validate all form fields
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    let isValid = true;

    // Validate each field
    newErrors.emailId = validateField('emailId', emailId);
    newErrors.name = validateField('name', name);
    
    // Only validate password if application access is enabled
    if (hasApplicationAccess) {
      newErrors.password = validateField('password', password);
    }
    
    newErrors.employeeStatus = validateField('employeeStatus', employeeStatus);
    newErrors.designation = validateField('designation', designation);
    newErrors.address = validateField('address', address);
    newErrors.city = validateField('city', city);
    newErrors.state = validateField('state', state);
    newErrors.pincode = validateField('pincode', pincode);
    
    // Validate date fields
    if (!dateOfBirth) {
      newErrors.dateOfBirth = 'Date of birth is required';
    }
    
    if (!joiningDate) {
      newErrors.joiningDate = 'Date of joining is required';
    }
    
    // Validate salary
    if (!salary) {
      newErrors.salary = 'Salary is required';
    }

    // Check for any validation errors
    for (const error of Object.values(newErrors)) {
      if (error) {
        isValid = false;
        break;
      }
    }

    // Mark all fields as touched
    const allTouched: Record<string, boolean> = {};
    Object.keys(newErrors).forEach(field => {
      allTouched[field] = true;
    });

    setErrors(newErrors);
    setTouched(allTouched);

    return isValid;
  };

  // Submit form data
  const handleSubmit = () => {
    // Validate all fields before submitting
    const validationErrors = validateForm();
    
    // Update all fields as touched for UI feedback
    setTouched({
      emailId: true,
      name: true,
      password: true,
      employeeStatus: true,
      designation: true,
      address: true,
      city: true,
      state: true,
      pincode: true,
      photo: true,
      dateOfBirth: true,
      joiningDate: true,
      salary: true,
      managerId: true,
    });

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      // Show toast for each error
      Object.values(validationErrors).forEach(errorMsg => {
        toast.error(errorMsg);
      });
      return;
    }

    // Get designation ID from name
    const designationId = getDesignationId(designation);
    
    // Ensure we have a valid designationId
    if (!designationId) {
      toast.error('Please select a valid designation');
      return;
    }
    
    // Process permission codes based on designation
    // But only if we've loaded designation permissions
    let permissionCodes = [];
    if (designationPermissions) {
      console.log('Processing designationPermissions:', designationPermissions);
      // Check if it's an array
      if (Array.isArray(designationPermissions)) {
        permissionCodes = designationPermissions.map(p => p.code);
      } 
      // Check if it has a permissions property that's an array
      else if (designationPermissions.permissions && Array.isArray(designationPermissions.permissions)) {
        permissionCodes = designationPermissions.permissions.map(p => p.code);
      }
      // Check if it's an object with codes directly
      else if (typeof designationPermissions === 'object') {
        // Extract codes from the object if possible
        const codes = Object.keys(designationPermissions)
          .filter(key => key !== 'applicationAccess' && designationPermissions[key] === true)
          .map(key => key);
        
        if (codes.length > 0) {
          permissionCodes = codes;
        }
      }
    }
      
    // Include application access from state
    const applicationAccess = hasApplicationAccess;

    // Get the S3 URL from the photo object if available
    const profilePictureUrl = photo && (photo as any).s3Url ? (photo as any).s3Url : undefined;

    // Build employee data
    const employeeData = {
      fullname: name,
      email: emailId,
      password,
      designationId: designationId,
      employeeStatus: employeeStatus,
      dob: dateOfBirth || undefined,
      dateOfJoining: joiningDate || undefined,
      address: address ? [
        {
          address,
          city,
          state,
          pinCode: Number(pincode),
          role: 'EMPLOYEE',
        },
      ] : undefined,
      ctc: salary ? Number(salary) : undefined,
      permissionCodes,
      applicationAccess,
      education: education || undefined,
      certifications: certification || undefined,
      jobLocation: jobLocation || undefined,
      managerId: managerId || undefined,
      // Use the S3 URL from the photo object
      profilePicture: profilePictureUrl,
    };

    // Add debug log to see what's being sent
    console.log('Manager ID being sent:', managerId, 'Type:', typeof managerId);

    // Show loading toast
    const loadingToast = toast.loading('Creating employee...');

    // Log the data being sent to the server
    console.log('Sending employee data to server:', employeeData);

    dispatch(createEmployee(employeeData))
      .unwrap()
      .then(() => {
        toast.dismiss(loadingToast);
        toast.success('Employee created successfully!');
        navigate('/employees');
      })
      .catch(error => {
        // Dismiss loading toast
        toast.dismiss(loadingToast);
        
        // Handle structured errors from API
        if (error?.errors && Array.isArray(error.errors)) {
          error.errors.forEach(err => {
            toast.error(`${err.field}: ${err.message}`);
          });
        }
        // Handle generic error message from API
        else if (error?.message) {
          toast.error(error.message);
        }
        // Fallback to showing a generic error message if we can't extract structured error data
        else if (typeof error === 'string') {
          toast.error(error);
        } else if (error && error.message) {
          toast.error(`Error: ${error.message}`);
        } else {
          toast.error('Failed to create employee. Please try again.');
        }
      });
  };

  // Handle photo upload
  const handleImageUpload = (file: File) => {
    setPhoto(file);
    
    // Show loading toast
    const loadingToast = toast.loading('Uploading photo...');
    
    // Create a local object URL for preview
    const localPreview = URL.createObjectURL(file);
    setPhotoPreview(localPreview);
    
    // Use the uploadEmployeePhoto action to upload to S3
    dispatch(uploadEmployeePhoto(file))
      .unwrap()
      .then((fileUrl) => {
        toast.dismiss(loadingToast);
        console.log('Uploaded image URL:', fileUrl);
        
        // Store the S3 URL in a hidden field or state variable
        // but keep showing the local preview which we know works
        // We'll use the S3 URL when submitting the form
        setPhoto(file);
        
        // Don't revoke the local object URL - keep using it for preview
        // Don't replace the preview URL with the S3 URL
        
        // Store the S3 URL in a ref or data attribute for form submission
        if (fileUrl) {
          // Store the S3 URL as a data attribute on the file object
          file.s3Url = fileUrl;
        }
        
        // Clear any photo validation errors
        if (errors.photo) {
          setErrors(prev => ({ ...prev, photo: undefined }));
        }
      })
      .catch(error => {
        toast.dismiss(loadingToast);
        toast.error(error?.message || 'Failed to upload photo');
        // Keep the local preview on error
      });
  };

  const handleImageRemove = () => {
    setPhoto(null);
    setPhotoPreview(null);
    // Don't add error since profile picture is now optional
    if (errors.photo) {
      setErrors(prev => ({ ...prev, photo: undefined }));
    }
  };

  // States list - Only Indian states
  const indianStates = [
    '', // Empty option as first element
    'Andhra Pradesh',
    'Arunachal Pradesh',
    'Assam',
    'Bihar',
    'Chhattisgarh',
    'Goa',
    'Gujarat',
    'Haryana',
    'Himachal Pradesh',
    'Jharkhand',
    'Karnataka',
    'Kerala',
    'Madhya Pradesh',
    'Maharashtra',
    'Manipur',
    'Meghalaya',
    'Mizoram',
    'Nagaland',
    'Odisha',
    'Punjab',
    'Rajasthan',
    'Sikkim',
    'Tamil Nadu',
    'Telangana',
    'Tripura',
    'Uttar Pradesh',
    'Uttarakhand',
    'West Bengal',
    // Union Territories
    'Andaman and Nicobar Islands',
    'Chandigarh',
    'Dadra and Nagar Haveli and Daman and Diu',
    'Delhi (NCT)',
    'Jammu and Kashmir',
    'Ladakh',
    'Lakshadweep',
    'Puducherry',
  ];

  // We'll use this for the actual dropdown options
  const [stateOptions, setStateOptions] = useState<string[]>(indianStates);

  // Create designation options for dropdown, showing name but using ID as value
  const designationOptions =
    designations && designations.length > 0 ? ['', ...designations.map(d => d.name)] : [''];

  // Create manager options for dropdown, showing name but using ID as value
  const managerOptions = ['', ...potentialManagers.map(manager => manager.fullname)];

  // Function to find manager ID from name
  const getManagerId = (managerName: string) => {
    if (!managerName || !potentialManagers || potentialManagers.length === 0) return '';
    const selectedManager = potentialManagers.find(m => m.fullname === managerName);
    return selectedManager ? selectedManager.id : '';
  };

  // Function to find manager name from ID
  const getManagerName = (managerId: string) => {
    if (!managerId || !potentialManagers || potentialManagers.length === 0) return '';
    const selectedManager = potentialManagers.find(m => m.id === managerId);
    return selectedManager ? selectedManager.fullname : '';
  };

  // Handle manager selection
  const handleManagerChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const managerName = e.target.value;
    const managerId = getManagerId(managerName);
    console.log('Manager selected:', managerName, 'ID:', managerId, 'Type:', typeof managerId);
    setManagerId(managerId);

    // Validate after change
    setTouched(prev => ({ ...prev, managerId: true }));
    setErrors(prev => ({
      ...prev,
      managerId: undefined, // Manager is optional, so no validation errors
    }));
  };

  // Function to find designation ID from name
  const getDesignationId = (designationName: string) => {
    if (!designationName || !designations || designations.length === 0) return '';
    const selectedDesignation = designations.find(d => d.name === designationName);
    return selectedDesignation ? selectedDesignation.id : '';
  };

  // Handle designation selection
  const handleDesignationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const designationName = e.target.value;
    setDesignation(designationName);
    
    // Get the designation ID for permissions fetching
    const designationId = getDesignationId(designationName);

    // Reset password field when designation changes
    setPassword('');

    // Fetch permissions for the selected designation
    if (designationId) {
      fetchDesignationPermissions(designationId);
    } else {
      setDesignationPermissions(null);
      setHasApplicationAccess(false);
    }

    // Validate after change
    setTouched(prev => ({ ...prev, designation: true }));
    setErrors(prev => ({
      ...prev,
      designation: designationName ? undefined : 'Designation is required',
    }));
  };

  // Employee Status options from schema
  const statusOptions = [
    '',
    'ACTIVE',
    'INACTIVE',
    'PROBATION',
    'TERMINATED',
    'RESIGNED',
    'RETIRED',
    'ON_LEAVE',
  ];

  // Education options from schema
  const educationOptions = [
    '',
    'PRIMARY',
    'SECONDARY',
    'HIGH_SCHOOL',
    'BACHELORS',
    'MASTERS',
    'DOCTORATE',
    'DIPLOMA',
    'CERTIFICATION',
    'OTHER',
  ];

  // ValidationMessage component to display error messages
  const ValidationMessage = ({ message }: { message?: string | undefined }) => {
    if (!message) return null;
    return <p className="mt-1 text-xs text-red-500">{message}</p>;
  };

  return (
    <ComponentWrapper icon={<FiUser />} title="Add New Employee">
      <div className="bg-white rounded-lg p-6">
        <div className="flex flex-col gap-6 text-sm">
          <h3 className="font-bold text-2xl">Personal Information</h3>

          <div className="grid grid-cols-2 gap-6">
            <InputField
              label="Employee ID"
              type="text"
              placeholder="Will be auto-generated"
              value={employeeId}
              onChange={e => setEmployeeId(e.target.value)}
              icon={<GoHash />}
              labelClassName="text-[#8D99A5]"
              disabled={true}
              helpText="ID will be auto-generated after employee creation"
            />
            <InputField
              label="Email ID"
              type="email"
              placeholder="Enter Email Address"
              value={emailId}
              onChange={e => handleInputChange('emailId', e.target.value)}
              onBlur={() => handleBlur('emailId', emailId)}
              icon={<FiMail />}
              labelClassName="text-[#8D99A5]"
              error={touched.emailId && errors.emailId ? errors.emailId : undefined}
            />
          </div>

          <InputField
            label="Employee Name"
            type="text"
            placeholder="Enter Full Name"
            value={name}
            onChange={e => handleInputChange('name', e.target.value)}
            onBlur={() => handleBlur('name', name)}
            icon={<FiUser />}
            labelClassName="text-[#8D99A5] text-xs"
            error={touched.name && errors.name ? errors.name : undefined}
          />

          <InputField
            label="Address"
            type="text"
            placeholder="Enter Address"
            value={address}
            onChange={e => handleInputChange('address', e.target.value)}
            onBlur={() => handleBlur('address', address)}
            icon={<FiHome />}
            className="h-24"
            labelClassName="text-[#8D99A5] text-xs"
            error={touched.address && errors.address ? errors.address : undefined}
          />

          <div className="grid grid-cols-2 gap-6">
            <InputField
              label="City/Location"
              type="text"
              placeholder="Enter City"
              value={city}
              onChange={e => handleInputChange('city', e.target.value)}
              onBlur={() => handleBlur('city', city)}
              icon={<FiMapPin />}
              labelClassName="text-[#8D99A5] text-xs"
              error={touched.city && errors.city ? errors.city : undefined}
            />
            <InputField
              label="State"
              type="select"
              placeholder="Select State"
              value={state}
              onChange={e => handleInputChange('state', e.target.value)}
              onBlur={() => handleBlur('state', state)}
              options={stateOptions}
              icon={<FiMapPin />}
              labelClassName="text-[#8D99A5] text-xs"
              error={touched.state && errors.state ? errors.state : undefined}
            />
          </div>

          <div className="grid grid-cols-1 gap-6">
            <InputField
              label="Pin Code"
              type="text"
              placeholder="Enter Pin Code"
              value={pincode}
              onChange={e => handleInputChange('pincode', e.target.value)}
              onBlur={() => handleBlur('pincode', pincode)}
              icon={<FiMapPin />}
              labelClassName="text-[#8D99A5] text-xs"
              error={touched.pincode && errors.pincode ? errors.pincode : undefined}
            />
          </div>

          <InputField
            label="Date of Birth"
            type="date"
            placeholder="Select Date of Birth"
            value={dateOfBirth}
            onChange={e => handleInputChange('dateOfBirth', e.target.value)}
            icon={<FiCalendar />}
            labelClassName="text-[#8D99A5] text-xs"
            min="1900-01-01"
            max="2023-12-31"
          />

          <div className={`${errors.photo ? 'border border-red-500 rounded-lg p-2' : ''}`}>
            <ImageUploader
              onImageUpload={handleImageUpload}
              onImageRemove={handleImageRemove}
              previewUrl={photoPreview}
              title="Drop employee photo here, or click to upload"
              dragActiveTitle="Drop photo here"
            />
            {errors.photo && touched.photo && (
              <div className="text-red-500 text-xs mt-1">{errors.photo}</div>
            )}
          </div>

          <h3 className="font-bold text-2xl pt-4">Job Related Information</h3>

          <div className="grid grid-cols-2 gap-6">
            <InputField
              label="Date of Joining"
              type="date"
              placeholder="Select Joining Date"
              value={joiningDate}
              onChange={e => handleInputChange('joiningDate', e.target.value)}
              icon={<FiCalendar />}
              labelClassName="text-[#8D99A5] text-xs"
              min="1900-01-01"
              max="2100-01-01"
            />
            <div>
              <InputField
                label="Designation"
                type="select"
                placeholder="Select Designation"
                value={designations?.find(d => d.id === designation)?.name || ''}
                onChange={handleDesignationChange}
                onBlur={() => handleBlur('designation', designation)}
                onFocus={() => {
                  console.log('Designation dropdown clicked - force refreshing designations data');
                  dispatch(forceRefreshDesignations());
                }}
                options={designationOptions}
                icon={<MdWork />}
                labelClassName="text-[#8D99A5] text-xs"
                disabled={designationsLoading}
                error={touched.designation && errors.designation ? errors.designation : undefined}
              />
              {loadingPermissions && (
                <p className="mt-1 text-xs text-blue-500">Loading designation permissions...</p>
              )}
              {designationsLoading && (
                <p className="mt-1 text-xs text-blue-500">Loading designations...</p>
              )}
            </div>
          </div>

          <InputField
            label="Yearly Salary (CTC)"
            type="text"
            placeholder="Enter Salary Amount"
            value={salary}
            onChange={e => handleInputChange('salary', e.target.value)}
            onBlur={() => handleBlur('salary', salary)}
            icon={<FaMoneyBillWave />}
            labelClassName="text-[#8D99A5] text-xs"
            error={touched.salary && errors.salary ? errors.salary : undefined}
          />

          <InputField
            label="Education"
            type="select"
            placeholder="Select Education Level"
            value={education}
            onChange={e => handleInputChange('education', e.target.value)}
            options={educationOptions}
            icon={<FaGraduationCap />}
            labelClassName="text-[#8D99A5] text-xs"
          />

          {/* <InputField
            label="Certification"
            type="text"
            placeholder="Enter Certification Details"
            value={certification}
            onChange={e => handleInputChange('certification', e.target.value)}
            icon={<FaCertificate />}
            labelClassName="text-[#8D99A5] text-xs"
          /> */}

          <h3 className="font-bold text-2xl pt-4">Employee Status</h3>

          <div className="grid grid-cols-2 gap-6">
            <InputField
              label="Status"
              type="select"
              placeholder="Select Status"
              value={employeeStatus}
              onChange={e => handleInputChange('employeeStatus', e.target.value)}
              onBlur={() => handleBlur('employeeStatus', employeeStatus)}
              options={statusOptions}
              icon={<MdWork />}
              labelClassName="text-[#8D99A5] text-xs"
              error={
                touched.employeeStatus && errors.employeeStatus ? errors.employeeStatus : undefined
              }
            />
            <InputField
              label="Job Location"
              type="text"
              placeholder="Enter Job Location"
              value={jobLocation}
              onChange={e => handleInputChange('jobLocation', e.target.value)}
              icon={<FaRegBuilding />}
              labelClassName="text-[#8D99A5] text-xs"
            />
          </div>

          {/* Display password field if designation has application access */}
          {hasApplicationAccess && (
            <div>
              <h3 className="font-bold text-2xl pt-4">Application Access</h3>
              <div className="mt-4">
                <InputField
                  label="Password"
                  type="password"
                  placeholder="Enter Password"
                  value={password}
                  onChange={e => handleInputChange('password', e.target.value)}
                  onBlur={() => handleBlur('password', password)}
                  icon={<FiLock />}
                  labelClassName="text-[#8D99A5] text-xs"
                  error={touched.password && errors.password ? errors.password : undefined}
                />
                <p className="mt-1 text-xs text-gray-500">
                  This designation has application access. A password is required.
                </p>
              </div>
            </div>
          )}

          {/* Manager Selection Dropdown */}
          <InputField
            label="Manager (Optional)"
            type="select"
            placeholder="Select Manager"
            value={getManagerName(managerId)}
            onChange={handleManagerChange}
            onBlur={() => handleBlur('managerId', managerId)}
            options={managerOptions}
            icon={<MdSupervisorAccount />}
            labelClassName="text-[#8D99A5] text-xs"
            disabled={loadingPotentialManagers}
            error={touched.managerId && errors.managerId ? errors.managerId : undefined}
            helpText={loadingPotentialManagers ? "Loading managers..." : "Only employees with management permission are shown"}
          />
        </div>
        <div className="mt-8 flex justify-end">
          <button
            onClick={handleSubmit}
            className="px-6 py-3 bg-lime-100 text-lg font-semibold rounded-lg hover:bg-lime-200 transition-colors 
            flex items-center border-2 w-full border-lime-600 justify-center gap-2 text-lime-700"
            disabled={loading || loadingPermissions}
          >
            {loading ? (
              <>
                <AiOutlineLoading3Quarters className="animate-spin mr-2" />
                Creating Employee...
              </>
            ) : loadingPermissions ? (
              <>
                <AiOutlineLoading3Quarters className="animate-spin mr-2" />
                Loading Permissions...
              </>
            ) : (
              'Add New Employee'
            )}
          </button>
        </div>
      </div>
    </ComponentWrapper>
  );
};

export default AddEmployee;
